/**
 * DEFCON TERMINAL - Phone Number Generator
 *
 * Generates fictional phone numbers in the 555-01XX format.
 * The 555 prefix is reserved for fiction/entertainment use only.
 *
 * @module PhoneGenerator
 */

import type { PathType } from '@shared/types';
import { PHONE_PREFIX, SYSTEM_NAMES } from '@shared/constants';

/**
 * Result of dialing a phone number
 */
export type DialResult =
  | 'NO_CARRIER'
  | 'VOICE'
  | 'BUSY'
  | 'CARRIER_DETECTED'
  | 'TIMEOUT'
  | 'ERROR';

/**
 * Information about a discovered system
 */
export interface DiscoveredSystem {
  phoneNumber: string;
  systemType: PathType;
  systemName: string;
  obfuscatedName: string;
  description: string;
  obfuscatedDescription: string;
  connectionBaudRate: number;
  discovered: boolean;
}

/**
 * Phone number with its dial result
 */
export interface PhoneEntry {
  number: string;
  result: DialResult;
  system?: DiscoveredSystem;
  dialTime: number; // ms it took to dial
}

/**
 * Seeded random number generator for reproducible results
 */
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  /**
   * Generate next random number between 0 and 1
   */
  next(): number {
    // Linear congruential generator
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  /**
   * Generate random integer between min and max (inclusive)
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /**
   * Select random element from array
   */
  choice<T>(arr: T[]): T {
    return arr[this.nextInt(0, arr.length - 1)];
  }
}

/**
 * Phone number generator for war dialer simulation
 */
export class PhoneGenerator {
  private rng: SeededRandom;
  private readonly systemNumbers: Map<string, PathType>;
  private readonly usedNumbers: Set<string>;
  private readonly totalNumbers: number = 100; // 555-0100 to 555-0199

  constructor(seed?: number) {
    // Use timestamp-based seed if none provided
    this.seed = seed ?? Date.now();
    this.rng = new SeededRandom(this.seed);
    this.systemNumbers = new Map();
    this.usedNumbers = new Set();

    // Pre-assign system numbers
    this.assignSystemNumbers();
  }

  private seed: number;

  /**
   * Get the current seed for saving/loading
   */
  getSeed(): number {
    return this.seed;
  }

  /**
   * Reset with a new seed
   */
  reset(seed?: number): void {
    this.seed = seed ?? Date.now();
    this.rng = new SeededRandom(this.seed);
    this.systemNumbers.clear();
    this.usedNumbers.clear();
    this.assignSystemNumbers();
  }

  /**
   * Assign phone numbers to the four game systems
   */
  private assignSystemNumbers(): void {
    const systems: PathType[] = ['sarn-west', 'sarn-east', 'phantom-directive', 'red-shadow'];
    const assignedNumbers = new Set<number>();

    for (const system of systems) {
      let number: number;
      do {
        // Generate number in range 00-99
        number = this.rng.nextInt(0, 99);
      } while (assignedNumbers.has(number));

      assignedNumbers.add(number);
      const phoneNumber = this.formatPhoneNumber(number);
      this.systemNumbers.set(phoneNumber, system);
    }
  }

  /**
   * Format a number (0-99) as a full phone number
   */
  private formatPhoneNumber(num: number): string {
    const suffix = num.toString().padStart(2, '0');
    return `${PHONE_PREFIX}${suffix}`;
  }

  /**
   * Generate all phone numbers in sequence
   */
  generateAllNumbers(): string[] {
    const numbers: string[] = [];
    for (let i = 0; i < this.totalNumbers; i++) {
      numbers.push(this.formatPhoneNumber(i));
    }
    return numbers;
  }

  /**
   * Get the dial result for a phone number
   */
  getDialResult(phoneNumber: string): DialResult {
    // Check if it's a system number
    if (this.systemNumbers.has(phoneNumber)) {
      return 'CARRIER_DETECTED';
    }

    // Generate weighted random result
    const roll = this.rng.next();

    if (roll < 0.60) {
      return 'NO_CARRIER';  // 60% - Most numbers don't answer
    } else if (roll < 0.75) {
      return 'VOICE';       // 15% - Human voice
    } else if (roll < 0.90) {
      return 'BUSY';        // 15% - Busy signal
    } else if (roll < 0.95) {
      return 'TIMEOUT';     // 5% - Connection timeout
    } else {
      return 'ERROR';       // 5% - Line error
    }
  }

  /**
   * Get system information for a carrier-detected number
   */
  getSystemInfo(phoneNumber: string): DiscoveredSystem | null {
    const systemType = this.systemNumbers.get(phoneNumber);
    if (!systemType) return null;

    const systemConfig = SYSTEM_NAMES[systemType];

    const systemDescriptions: Record<PathType, { description: string; baudRate: number }> = {
      'sarn-west': {
        description: 'US STRATEGIC COMMAND - NORAD INTERFACE',
        baudRate: 9600,
      },
      'sarn-east': {
        description: 'SOVIET STRATEGIC COMMAND - BACKUP TERMINAL',
        baudRate: 4800,
      },
      'phantom-directive': {
        description: 'CIA SPECIAL ACTIVITIES CENTER',
        baudRate: 14400,
      },
      'red-shadow': {
        description: 'KGB DIRECTORATE S - FIELD TERMINAL',
        baudRate: 2400,
      },
    };

    const info = systemDescriptions[systemType];

    const displayName = 'englishName' in systemConfig && systemConfig.englishName
      ? systemConfig.englishName
      : systemConfig.name;

    return {
      phoneNumber,
      systemType,
      systemName: displayName,
      obfuscatedName: this.obfuscateString(displayName),
      description: info.description,
      obfuscatedDescription: this.obfuscateString(info.description),
      connectionBaudRate: info.baudRate,
      discovered: false,
    };
  }

  /**
   * Obfuscate a string for initial display (partial redaction)
   */
  private obfuscateString(str: string): string {
    return str.split('').map((char, idx) => {
      if (char === ' ' || char === '-' || char === '.') return char;
      // Show first character, then random obfuscation
      if (idx === 0) return char;
      return this.rng.next() > 0.7 ? char : '*';
    }).join('');
  }

  /**
   * Get all system phone numbers (for testing/debugging)
   */
  getSystemNumbers(): Map<string, PathType> {
    return new Map(this.systemNumbers);
  }

  /**
   * Check if a phone number is a system number
   */
  isSystemNumber(phoneNumber: string): boolean {
    return this.systemNumbers.has(phoneNumber);
  }

  /**
   * Get friendly display text for a dial result
   */
  static getResultDisplay(result: DialResult): string {
    const displays: Record<DialResult, string> = {
      'NO_CARRIER': 'NO CARRIER',
      'VOICE': 'VOICE (HUMAN)',
      'BUSY': 'BUSY',
      'CARRIER_DETECTED': '\u2588\u2588\u2588\u2588 CARRIER DETECTED \u2588\u2588\u2588\u2588',
      'TIMEOUT': 'TIMEOUT',
      'ERROR': 'LINE ERROR',
    };
    return displays[result];
  }

  /**
   * Get the dial time for a result (simulated connection time)
   */
  getDialTime(result: DialResult): number {
    const baseTimes: Record<DialResult, [number, number]> = {
      'NO_CARRIER': [1500, 3000],
      'VOICE': [800, 1500],
      'BUSY': [500, 1000],
      'CARRIER_DETECTED': [2000, 4000],
      'TIMEOUT': [5000, 8000],
      'ERROR': [1000, 2000],
    };

    const [min, max] = baseTimes[result];
    return this.rng.nextInt(min, max);
  }
}

export default PhoneGenerator;
